'use client'

import type {
  CartItemType,
} from '../../lib/utils/cart'
import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { toast } from 'sonner'
import { usePathname } from '../../i18n/routing'
import { trackEvent } from '../../lib/analytics/client'
import { parseAnalyticsNumber } from '../../lib/analytics/events'
import { routes } from '../../lib/constants'
import useDeleteCartItem from '../../lib/hooks/cart/use-delete-cart-item'
import useGetCart from '../../lib/hooks/cart/use-get-cart'
import useSendCartQuoteRequest from '../../lib/hooks/cart/use-send-cart-quote-request'
import { cn } from '../../lib/utils'
import {
  mapApiCartItemToCartItemType,
} from '../../lib/utils/cart'
import { Sheet, SheetContent } from '../common/components/ui/sheet'
import { BinIcon } from '../common/icons/bin'
import { useDynamicSvg } from '../proposals/configurator/hooks/use-dynamic-svg'
import { CartStepper } from './cart-stepper/cart-stepper'
import { CountrySelector } from './common/country-selector'
import { isSpainCountry, normalizeShippingCountry } from './common/country-utils'
import { ProvinceSelector } from './common/province-selector'
import { EmptyCart } from './empty-cart'
import { OrderSummary as ReusableOrderSummary } from './order-summary'

interface CartQuoteRequestFormState {
  firstName: string
  lastName: string
  email: string
  phone: string
  street: string
  number: string
  postalCode: string
  country: string
  province: string
  locality: string
}

type CartQuoteRequestFormErrors = Partial<Record<keyof CartQuoteRequestFormState, string>>

interface CartQuoteRequestValidationMessages {
  firstName: string
  lastName: string
  email: string
  country: string
  province: string
  locality: string
}

const initialQuoteRequestFormState: CartQuoteRequestFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  number: '',
  postalCode: '',
  country: 'España',
  province: '',
  locality: '',
}

function validateQuoteRequestForm(
  values: CartQuoteRequestFormState,
  messages: CartQuoteRequestValidationMessages,
): CartQuoteRequestFormErrors {
  const errors: CartQuoteRequestFormErrors = {}
  const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

  if (values.firstName.trim().length < 2) {
    errors.firstName = messages.firstName
  }
  if (values.lastName.trim().length < 2) {
    errors.lastName = messages.lastName
  }
  if (!emailRegex.test(values.email.trim())) {
    errors.email = messages.email
  }
  if (!values.country.trim()) {
    errors.country = messages.country
  }
  if (values.province.trim().length < 2) {
    errors.province = messages.province
  }
  if (values.locality.trim().length < 2) {
    errors.locality = messages.locality
  }

  return errors
}

function CartQuoteRequestCard({
  provinces,
}: {
  provinces: string[]
}) {
  const t = useTranslations('common.cart.quoteRequest')
  const locale = useLocale()
  const [isOpen, setIsOpen] = useState(false)
  const [formValues, setFormValues] = useState<CartQuoteRequestFormState>(
    initialQuoteRequestFormState,
  )
  const [formErrors, setFormErrors] = useState<CartQuoteRequestFormErrors>({})
  const quoteRequestMutation = useSendCartQuoteRequest()
  const usesSpanishProvinceSelector = isSpainCountry(formValues.country)
  const selectableProvinces = useMemo(
    () => provinces.filter(province => !isSpainCountry(province)),
    [provinces],
  )
  const validationMessages = useMemo<CartQuoteRequestValidationMessages>(
    () => ({
      firstName: t('validation.firstName'),
      lastName: t('validation.lastName'),
      email: t('validation.email'),
      country: t('validation.country'),
      province: t('validation.province'),
      locality: t('validation.locality'),
    }),
    [t],
  )

  const updateField = (
    field: keyof CartQuoteRequestFormState,
    value: string,
  ) => {
    setFormValues(current => ({ ...current, [field]: value }))
    setFormErrors(current => ({ ...current, [field]: undefined }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextErrors = validateQuoteRequestForm(formValues, validationMessages)
    setFormErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    quoteRequestMutation.mutate(
      {
        first_name: formValues.firstName.trim(),
        last_name: formValues.lastName.trim(),
        email: formValues.email.trim(),
        locale,
        phone: formValues.phone.trim() || undefined,
        street: formValues.street.trim() || undefined,
        number: formValues.number.trim() || undefined,
        postal_code: formValues.postalCode.trim() || undefined,
        country: normalizeShippingCountry(formValues.country),
        province: formValues.province.trim(),
        locality: formValues.locality.trim(),
      },
      {
        onSuccess: () => {
          toast.success(t('toast.successTitle'), {
            description: t('toast.successDescription'),
            classNames: {
              description: '!text-black',
            },
          })
          setIsOpen(false)
          setFormValues(initialQuoteRequestFormState)
        },
        onError: () => {
          toast.error(t('toast.errorTitle'), {
            description: t('toast.errorDescription'),
          })
        },
      },
    )
  }

  return (
    <>
      <section className="mt-6 rounded-2xl bg-[#f6f6f6] p-6">
        <h2 className="mb-3 text-[21px] font-bold text-stone-900">
          {t('cardTitle')}
        </h2>
        <p className="mb-5 text-[15px] leading-6 text-stone-700">
          {t('cardDescription')}
        </p>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="w-full rounded-xl bg-stone-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-700"
        >
          {t('cardButton')}
        </button>
      </section>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          dismissible={false}
          title={t('sheetAccessibleTitle')}
          side="right"
          className="w-full overflow-y-auto sm:max-w-md"
        >
          <form className="flex min-h-full flex-col gap-4 pb-5" onSubmit={handleSubmit}>
            <div className="pr-10">
              <h2 className="text-2xl font-bold leading-8 text-stone-900">
                {t('sheetTitle')}
              </h2>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-stone-800">
                {t('fields.firstName')}
                <input
                  value={formValues.firstName}
                  onChange={event => updateField('firstName', event.target.value)}
                  className={cn(
                    'mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900',
                    formErrors.firstName && 'border-red-500 focus:ring-red-500',
                  )}
                />
                {formErrors.firstName ? (
                  <span className="mt-1 block text-xs text-red-600">
                    {formErrors.firstName}
                  </span>
                ) : null}
              </label>

              <label className="block text-sm font-medium text-stone-800">
                {t('fields.lastName')}
                <input
                  value={formValues.lastName}
                  onChange={event => updateField('lastName', event.target.value)}
                  className={cn(
                    'mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900',
                    formErrors.lastName && 'border-red-500 focus:ring-red-500',
                  )}
                />
                {formErrors.lastName ? (
                  <span className="mt-1 block text-xs text-red-600">
                    {formErrors.lastName}
                  </span>
                ) : null}
              </label>

              <label className="block text-sm font-medium text-stone-800">
                {t('fields.email')}
                <input
                  type="email"
                  value={formValues.email}
                  onChange={event => updateField('email', event.target.value)}
                  className={cn(
                    'mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900',
                    formErrors.email && 'border-red-500 focus:ring-red-500',
                  )}
                />
                {formErrors.email ? (
                  <span className="mt-1 block text-xs text-red-600">
                    {formErrors.email}
                  </span>
                ) : null}
              </label>

              <label className="block text-sm font-medium text-stone-800">
                {t('fields.phoneOptional')}
                <input
                  value={formValues.phone}
                  onChange={event => updateField('phone', event.target.value)}
                  className="mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900"
                />
              </label>

              <label className="block text-sm font-medium text-stone-800">
                {t('fields.street')}
                <input
                  value={formValues.street}
                  onChange={event => updateField('street', event.target.value)}
                  className="mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm font-medium text-stone-800">
                  {t('fields.number')}
                  <input
                    value={formValues.number}
                    onChange={event => updateField('number', event.target.value)}
                    className="mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900"
                  />
                </label>

                <label className="block text-sm font-medium text-stone-800">
                  {t('fields.postalCode')}
                  <input
                    value={formValues.postalCode}
                    onChange={event => updateField('postalCode', event.target.value)}
                    className="mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="block text-sm font-medium text-stone-800">
                  {t('fields.country')}
                  <CountrySelector
                    value={formValues.country}
                    onChange={(value) => {
                      updateField('country', value)
                      updateField('province', '')
                    }}
                    mode="name"
                    className={cn(
                      'mt-1.5 !h-[42px] !rounded-lg !px-3',
                      formErrors.country && 'border-red-500 focus:ring-red-500',
                    )}
                  />
                  {formErrors.country ? (
                    <span className="mt-1 block text-xs text-red-600">
                      {formErrors.country}
                    </span>
                  ) : null}
                </div>

                <div className="block text-sm font-medium text-stone-800">
                  {t('fields.province')}
                  {usesSpanishProvinceSelector ? (
                    <ProvinceSelector
                      value={formValues.province}
                      onChange={value => updateField('province', value)}
                      provinces={selectableProvinces}
                      placeholderSelect={t('fields.selectProvince')}
                      placeholderSearch={t('fields.searchProvince')}
                      placeholderEmpty={t('fields.noProvincesFound')}
                      className={cn(
                        'mt-1.5 !h-[42px] !rounded-lg !px-3',
                        formErrors.province && 'border-red-500 focus:ring-red-500',
                      )}
                    />
                  ) : (
                    <input
                      value={formValues.province}
                      onChange={event => updateField('province', event.target.value)}
                      className={cn(
                        'mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900',
                        formErrors.province && 'border-red-500 focus:ring-red-500',
                      )}
                    />
                  )}
                  {formErrors.province ? (
                    <span className="mt-1 block text-xs text-red-600">
                      {formErrors.province}
                    </span>
                  ) : null}
                </div>
              </div>

              <label className="block text-sm font-medium text-stone-800">
                {t('fields.locality')}
                <input
                  value={formValues.locality}
                  onChange={event => updateField('locality', event.target.value)}
                  className={cn(
                    'mt-1.5 h-[42px] w-full rounded-lg border border-black bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-stone-900',
                    formErrors.locality && 'border-red-500 focus:ring-red-500',
                  )}
                />
                {formErrors.locality ? (
                  <span className="mt-1 block text-xs text-red-600">
                    {formErrors.locality}
                  </span>
                ) : null}
              </label>
            </div>

            <button
              type="submit"
              disabled={quoteRequestMutation.isPending}
              className="mt-auto h-[48px] w-full shrink-0 rounded-lg bg-stone-900 px-5 text-sm font-semibold text-white transition-colors hover:bg-stone-700 disabled:cursor-not-allowed disabled:bg-stone-400"
            >
              {quoteRequestMutation.isPending
                ? t('submittingButton')
                : t('submitButton')}
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  )
}

// CartItemImage component - uses dynamic SVG when tones are available
const CartItemImage: React.FC<{
  item: CartItemType
}> = ({ item }) => {
  // Check if we should use dynamic SVG (only for design items with SVG and tones)
  const shouldUseDynamicSvg = !!(
    item.imageUrl?.endsWith('.svg')
    && item.tones
    && item.tones.length > 0
  )

  // Use the dynamic SVG hook
  const { dataUri } = useDynamicSvg({
    src: shouldUseDynamicSvg ? item.imageUrl! : '',
    initialTones: shouldUseDynamicSvg ? item.tones! : [],
  })

  // Use dataUri if available, otherwise fall back to imageUrl
  const imageSrc = shouldUseDynamicSvg && dataUri ? dataUri : item.imageUrl

  return (
    <img
      src={imageSrc}
      alt={item.name}
      className="h-auto w-full object-contain"
    />
  )
}

// Internal Components

export const CartItem: React.FC<{
  item: CartItemType
  customClassName?: string
}> = ({ item, customClassName }) => {
  const t = useTranslations('common.cart')
  const tz = useTranslations('common.cart.zellige')
  const path = usePathname()
  const deleteCartItemMutation = useDeleteCartItem()

  const handleDeleteItem = useCallback(() => {
    deleteCartItemMutation.mutate(item.id, {
      onSuccess: () => {
        trackEvent('car_list_eliminar_item', {
          item_id: item.id,
          item_name: item.fullName,
          item_type: item.itemType,
          quantity: item.quantity
            ? parseAnalyticsNumber(item.quantity)
            : undefined,
          unit: item.unit,
        })
      },
    })
  }, [
    deleteCartItemMutation,
    item.fullName,
    item.id,
    item.itemType,
    item.quantity,
    item.unit,
  ])

  // Show visual feedback during optimistic update
  const isDeleting = deleteCartItemMutation.isPending
  const hasError = deleteCartItemMutation.isError

  // Show toast notifications for errors
  useEffect(() => {
    if (hasError) {
      toast.error('Error al eliminar el producto del carrito', {
        description: 'El producto se ha restaurado. Inténtalo de nuevo.',
        action: {
          label: 'Reintentar',
          onClick: () => handleDeleteItem(),
        },
      })
    }
  }, [hasError, handleDeleteItem])
  const roundingInfo = (() => {
    if (item.itemType !== 'zellige' || !item.m2PerBox || item.m2PerBox <= 0) {
      return null
    }
    const rawValue = item.area || item.quantity
    if (!rawValue)
      return null
    const val = parseFloat(rawValue.replace(',', '.'))
    if (Number.isNaN(val) || val <= 0)
      return null

    const boxesNeeded = Math.ceil(val / item.m2PerBox)
    const realM2Value = boxesNeeded * item.m2PerBox
    const realM2 = realM2Value.toLocaleString('es-ES', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    })
    const boxLabel = boxesNeeded > 1 ? tz('boxPlural') : tz('boxSingular')

    return tz('boxEquivalence', {
      boxes: boxesNeeded,
      boxLabel,
      realM2,
    })
  })()

  return (
    <article
      className={cn(
        'flex flex-col items-center gap-5 p-5 mb-4 rounded-2xl bg-[#f6f6f6] transition-all duration-300 min-[1200px]:flex-row',
        hasError && 'border-2 border-red-300 bg-red-50',
      )}
    >
      {/* Image Section */}
      <div className="shrink-0 flex w-full max-w-[250px] justify-center min-[1200px]:block min-[1200px]:w-[clamp(72px,calc(72px+78*((100vw-1200px)/265)),150px)] min-[1200px]:max-w-none min-[1200px]:h-[clamp(72px,calc(72px+78*((100vw-1200px)/265)),150px)] min-[1465px]:w-[150px] min-[1465px]:h-[150px]">
        <CartItemImage item={item} />
      </div>

      {/* Content Section */}
      <div className="flex-1 w-full text-center min-[1200px]:text-left">
        <h3 className="mb-3 md:mb-0 text-xl font-bold max-sm:text-lg capitalize">
          {item.fullName}
        </h3>

        {/* Desktop Layout */}
        <div
          className={cn(
            'hidden min-[1200px]:flex gap-[27px] flex-1 justify-between',
            customClassName,
          )}
        >
          <div className="md:w-auto text-ellipsis">
            <p className="mb-3 text-base text-stone-900 max-w-[103px]   whitespace-pre capitalize">
              {item.description}
            </p>
          </div>
          <div className="grid grid-cols-[auto_auto] gap-x-[34px] self-center md:min-w-[266px] w-[70%]">
            <div>
              <p className="text-[17px] text-stone-900 leading-[25px]">
                {item.area
                  ? t('pricePerSquareMeterMain', {
                      defaultValue: 'Precio m²',
                    })
                  : t('pricePerUnitMain', {
                      defaultValue: 'Precio unidad',
                    })}
                <span className="text-[11px] ml-1">
                  {t('exclVAT', {
                    defaultValue: '(excl. IVA)',
                  })}
                </span>
              </p>
              <p className="text-[17px] text-stone-900 leading-[25px]">
                {t('quantity', { defaultValue: 'Cantidad' })}
              </p>
              <p className="text-[17px] text-stone-900 leading-[25px]">
                {t('totalExclTaxMain', {
                  defaultValue: 'Total',
                })}
                <span className="text-[11px] ml-1">
                  {t('exclVATAndShipping', {
                    defaultValue: '(excl. IVA y transporte)',
                  })}
                </span>
              </p>
            </div>

            <div>
              <div>
                <p className="text-[17px] font-bold leading-[25px]">
                  {item.price}
                </p>
                <p className="text-[17px] font-bold leading-[25px]">
                  {item.unit === 'm2' || item.unit === 'm²'
                    ? `${item.area || item.quantity} m²`
                    : `${item.quantity} ud`}
                </p>
                <p className="text-[17px] font-bold leading-[25px]">
                  {item.totalPrice || '0,00 €'}
                </p>
              </div>
            </div>
            {roundingInfo && (
              <p className="col-span-2 text-xs text-black/60 font-medium ml-1">
                {roundingInfo}
              </p>
            )}
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="min-[1200px]:hidden space-y-4">
          <p className="text-sm text-stone-900 mb-4">{item.description}</p>

          <div className="space-y-3 max-w-[570px] m-auto">
            <div className="flex justify-between items-center">
              <span className="text-sm text-stone-900">
                {item.area
                  ? t('pricePerSquareMeterMain', {
                      defaultValue: 'Precio m²',
                    })
                  : t('pricePerUnitMain', {
                      defaultValue: 'Precio unidad',
                    })}
                <span className="text-[11px] ml-1">
                  {t('exclVAT', {
                    defaultValue: '(excl. IVA)',
                  })}
                </span>
              </span>
              <span className="text-sm font-bold w-[70px] text-right">
                {item.price}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-stone-900">
                {t('quantity', { defaultValue: 'Cantidad' })}
              </span>
              <span className="text-sm font-bold w-[70px] text-right">
                {item.unit === 'm2' || item.unit === 'm²'
                  ? `${item.area || item.quantity} m²`
                  : `${item.quantity} ud`}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-stone-900 text-left">
                {t('totalExclTaxMain', {
                  defaultValue: 'Total',
                })}
                <span className="text-[11px] ml-1">
                  {t('exclVATAndShipping', {
                    defaultValue: '(excl. IVA y transporte)',
                  })}
                </span>
              </span>
              <span className="text-sm font-bold w-auto text-right">
                {item.totalPrice || '0,00 €'}
              </span>
            </div>
            {roundingInfo && (
              <p className="text-xs text-black/60 font-medium ml-1">
                {roundingInfo}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {path === routes.cart && (
        <div className="shrink-0 flex w-full flex-row justify-center gap-2.5 self-center mt-4 min-[1200px]:mt-0 min-[1200px]:ml-4 min-[1200px]:w-auto min-[1200px]:flex-col min-[1200px]:justify-center">
          <button
            className={cn(
              'rounded-xl border border-solid cursor-pointer size-10 transition-all duration-200 flex items-center justify-center',
              isDeleting
                ? 'border-gray-400 text-gray-500 bg-gray-100'
                : hasError
                  ? 'border-red-500 text-red-600 bg-red-50 hover:bg-red-100'
                  : 'border-black text-black hover:bg-gray-100',
            )}
            onClick={handleDeleteItem}
            aria-label={t('delete', { defaultValue: 'Eliminar' })}
            title={
              hasError
                ? t('deleteRetryError', {
                    defaultValue: 'Error al eliminar. Haz click para reintentar.',
                  })
                : t('delete', { defaultValue: 'Eliminar' })
            }
          >
            <BinIcon className="size-[18px]" aria-hidden="true" />
          </button>
        </div>
      )}
    </article>
  )
}

// Re-export the OrderSummary component with default props for backward compatibility
export const OrderSummary: React.FC<{
  cartItems: CartItemType[]
  route: string
}> = ({ cartItems, route }) => {
  return (
    <ReusableOrderSummary
      cartItems={cartItems}
      route={route}
      state="shopping"
    />
  )
}

// Main Component
const ShoppingCartSingle: React.FC = () => {
  const t = useTranslations('common.cart')
  const { data: cartData, isLoading, error } = useGetCart()

  // Map API cart items to frontend CartItemType
  const cartItems
    = cartData?.items?.map(item =>
      mapApiCartItemToCartItemType(item, {
        productWithoutDescription: t('productWithoutDescription'),
        productWithoutName: t('productWithoutName'),
        thickness: t('thickness'),
      }),
    ) || []

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-xl text-stone-600">
          {t('loadingCart', { defaultValue: 'Cargando carrito...' })}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 text-xl text-red-600">
          {t('errorLoadingCart', {
            defaultValue: 'Error al cargar el carrito',
          })}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-amber-300 rounded-xl hover:bg-amber-400 transition-colors"
        >
          {t('retry', { defaultValue: 'Reintentar' })}
        </button>
      </div>
    )
  }

  return (
    <main className="mx-auto my-0 bg-white pb-6 min-h-screen h-full">
      <div className="px-5 py-0 min-[640px]:px-10 min-[1200px]:px-32">
        <h1 className="mb-10 text-4xl font-bold text-center leading-[40px] text-stone-900 min-[640px]:text-[67px] min-[640px]:leading-[70px]">
          {t('myCart', { defaultValue: 'Carrito' })}
        </h1>

        <CartStepper />

        {cartItems.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="flex flex-col gap-6 min-[1200px]:flex-row min-[1200px]:gap-10">
            <div className="flex-[2]">
              {cartItems.map((item, index) => (
                <CartItem key={index} item={item} />
              ))}
            </div>

            <div className="flex-1 order-[summary]">
              <OrderSummary cartItems={cartItems} route={routes.address} />
              <CartQuoteRequestCard provinces={cartData?.provinces ?? []} />
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

export default ShoppingCartSingle
